import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
// Stores
import PerformanceStore from 'stores/PerformanceStore';
import AccountStore from 'stores/AccountStore';
import SiteStore from 'stores/SiteStore';
import LoggedUserStore from 'stores/LoggedUserStore';
// Actions
import PerformanceAction from 'actions/PerformanceAction';
import AccountAction from 'actions/AccountAction';
// Locales
import translate from 'locales/i18n';
// Material ui
import { withStyles } from '@mui/styles';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { CircularProgress } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import PreviewIcon from '@mui/icons-material/Preview';
import IconButton from '@mui/material/IconButton';
// Pcp components
import PerformancesTable from 'components/tables/PerformancesTable/PerformancesTable';
import DialogEditPerformance from 'components/dialogs/performance/DialogEditPerformance';
//Style
import styles from 'components/page/PerformancePage/PerformancePage.style';
import DialogPerformanceGraph from 'components/dialogs/performance/DialogPerformanceGraph';

class Performance extends React.Component {

    state = {
        from: PerformanceStore.getParams().from ? PerformanceStore.getParams().from : new Date(),
        to: PerformanceStore.getParams().to ? PerformanceStore.getParams().to : new Date(),
        isLoadingPerformances: PerformanceStore.isLoading(),
        isLoadingAccounts: AccountStore.isLoading(),
        performanceSites: PerformanceStore.getSites(),
        sites: SiteStore.getSites(),
        accounts: AccountStore.getAccounts(),
        accountName: PerformanceStore.getParams().bySites ? '' : PerformanceStore.getParams().search,
        siteName: PerformanceStore.getParams().bySites ? PerformanceStore.getParams().search : '',
        currentPerformance: {
            siteId: null,
            orderCount: 0,
            date: new Date(),
            account_ids_visibility: []
        },
        dialogEditPerformanceOpen: false,
        bySites: PerformanceStore.getParams().bySites,
        openPreview: false
    };

    static propTypes = {
        classes: PropTypes.object.isRequired,
    };

    componentDidMount() {
        PerformanceStore.addChangeListener(this.onPerformanceStoreChange);
        AccountStore.addChangeListener(this.onAccountStoreChange);
        SiteStore.addChangeListener(this.onSiteStoreChange);

        if (AccountStore.getAccounts().length === 0 && LoggedUserStore.isLogged())
            AccountAction.getAccounts();

        this.mounted = true;
    }

    componentWillUnmount() {
        PerformanceStore.removeChangeListener(this.onPerformanceStoreChange);
        AccountStore.removeChangeListener(this.onAccountStoreChange);
        SiteStore.removeChangeListener(this.onSiteStoreChange);

        this.mounted = false;
    }

    onPerformanceStoreChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            performanceSites: PerformanceStore.getSites(),
            isLoadingPerformances: PerformanceStore.isLoading()
        });
    };

    onAccountStoreChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            accounts: AccountStore.getAccounts(),
            isLoadingAccounts: AccountStore.isLoading()
        });
    };

    onSiteStoreChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            sites: SiteStore.getSites()
        });
    };

    getPerformances = bySites => {
        const options = {
            search: bySites ? this.state.siteName : this.state.accountName,
            from: this.state.from,
            to: this.state.to
        };

        PerformanceAction.getPerformances(options, bySites);
    };

    handleChangeSite = event => {
        this.setState({ siteName: this.state.sites.find((site) => site.name === event.target.value).name }, () => this.getPerformances(true));
    };

    handleChangeAccount = event => {
        this.setState({ accountName: this.state.accounts.find((account) => account.name === event.target.value).name }, () => this.getPerformances(false));
    };

    handleStartDate = (date) => {
        this.setState({ from: date }, () => {
            this.getPerformances();
        });
    };

    handleEndDate = (date) => {
        this.setState({ to: date }, () => {
            this.getPerformances();
        });
    };

    renderDialogEditPerformance = () =>
        <DialogEditPerformance close={() => {
            this.setState({
                dialogEditPerformanceOpen: false,
                currentPerformance: {
                    siteId: null,
                    orderCount: 0,
                    date: new Date(),
                    account_ids_visibility: []
                }
            });
        }}
        open={this.state.dialogEditPerformanceOpen}
        currentPerformance={this.state.currentPerformance}
        sites={this.state.performanceSites}
        />;

    renderDialogPerformaceGraph = () =>
        <DialogPerformanceGraph
            open={this.state.openPreview}
            close={() => this.setState({ openPreview: false })}
            perfs={this.state.performanceSites} />;

    renderAccountChoice = () => {
        const { classes } = this.props;
        const { accounts, accountName } = this.state;

        const performanceAccounts = accounts.filter(account => account.modules.perf);

        return (
            <div className={classes.root}>
                <FormControl>
                    <InputLabel>Account</InputLabel>
                    <Select
                        value={accountName ? '' : accountName}
                        onChange={this.handleChangeAccount}
                        label='Account'
                    >
                        <MenuItem value=''>
                            <em>None</em>
                        </MenuItem>
                        {performanceAccounts ?
                            performanceAccounts.map((account, key) => <MenuItem value={account.name} key={key}>{account.name}</MenuItem>)
                            :
                            null
                        }
                    </Select>
                    <FormHelperText>Select an account before managing perf data</FormHelperText>
                </FormControl>
            </div>
        );
    };

    render() {
        const { classes } = this.props;
        const { accountName, performanceSites, to, from, accounts, sites, isLoadingPerformances, bySites, siteName } = this.state;
        const display = !!performanceSites.find(site => site.records.length > 0);

        if (!LoggedUserStore.isLogged()) return <Redirect to='/login' />;

        if (!accountName) return this.renderAccountChoice();

        const performanceAccounts = accounts.filter(account => account.modules.perf);

        return (
            <div className={classes.root}>
                <div className={classes.topContainer}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            renderInput={(props) => <TextField {...props} size='small' classes={{ root: classes.datePicker }} />}
                            label='From'
                            value={from}
                            onChange={(newValue) => {
                                this.handleStartDate(newValue);
                            }}
                            inputVariant='outlined'
                            inputFormat='dd/MM/yyyy'
                            maxDate={to}
                            margin={'dense'}
                        />
                        <DatePicker
                            renderInput={(props) => <TextField {...props} size='small' classes={{ root: classes.datePicker }} />}
                            label='To'
                            value={to}
                            onChange={(newValue) => {
                                this.handleEndDate(newValue);
                            }}
                            inputVariant='outlined'
                            inputFormat='dd/MM/yyyy'
                            maxDate={new Date()}
                            minDate={from}
                            margin={'dense'}
                        />
                    </LocalizationProvider>
                    {bySites ?
                        <FormControl className={classes.formControl}>
                            <InputLabel>Sites</InputLabel>
                            <Select
                                value={siteName}
                                onChange={this.handleChangeSite}
                                label='Sites'
                            >
                                <MenuItem value=''>
                                    <em>None</em>
                                </MenuItem>
                                {sites ?
                                    sites.map((site, key) => <MenuItem value={site.name} key={key}>{site.name}</MenuItem>)
                                    :
                                    null
                                }
                            </Select>
                            <FormHelperText>Select a site</FormHelperText>
                        </FormControl>
                        :
                        <FormControl className={classes.formControl}>
                            <InputLabel>Accounts</InputLabel>
                            <Select
                                value={accountName}
                                onChange={this.handleChangeAccount}
                                label='Accounts'
                            >
                                <MenuItem value=''>
                                    <em>None</em>
                                </MenuItem>
                                {performanceAccounts ?
                                    performanceAccounts.map((account, key) => <MenuItem value={account.name} key={key}>{account.name}</MenuItem>)
                                    :
                                    null
                                }
                            </Select>
                            <FormHelperText>Select an account</FormHelperText>
                        </FormControl>
                    }
                    <IconButton onClick={() => this.setState({ openPreview: true })} style={{ marginRight: '10px' }}>
                        <PreviewIcon />
                    </IconButton>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={bySites}
                                onChange={() => this.setState({ bySites: !bySites })}
                                name='checkedB'
                                color='primary'
                            />
                        }
                        label='By sites'
                    />
                    <Button
                        variant='contained'
                        color='primary'
                        className={classes.button}
                        onClick={() => this.setState({ dialogEditPerformanceOpen: true })}>
                        <AddIcon className={classes.leftIcon} />
                        Performance
                    </Button>
                </div>
                { isLoadingPerformances ?
                    <div className={classes.loaderContainer}>
                        <CircularProgress className={classes.progress} />
                    </div>
                    :
                    !display ?
                        <div className={classes.noRecordsFoundContainer}>{translate.i18n('NO_RECORDS_FOUND')}</div>
                        :
                        <div className={classes.informationContainer}>
                            <PerformancesTable
                                sites={performanceSites}
                                to={to}
                                from={from}
                                openManagePerformanceDialog={currentPerformance => {
                                    this.setState({
                                        currentPerformance,
                                        dialogEditPerformanceOpen: true
                                    });
                                }} />
                        </div>
                }
                {this.renderDialogEditPerformance()}
                {this.renderDialogPerformaceGraph()}
            </div>
        );
    }
}

export default withStyles(styles)(Performance);
